import { type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InlineLink } from 'InlineLink';
import { Modal } from 'Modal';
import { Text } from 'Text';
import { navigationHelper } from 'NavigationHelpers';
import { TuitionPlanFrequency } from 'TuitionPlan';
import { LOCALE_PREFIX } from '../constants';

type PaymentOptionLike = {
    optionId: string;
    tuitionPlan?: { frequency: TuitionPlanFrequency; numIntervals: number | null };
    additionalFinancingOption?: unknown;
    paymentInformation: {
        netDiscountAmount?: number;
        formattedNetDiscountAmount?: string | null;
        // Only the financing options carry this. Listed so both option shapes share a property,
        // which TypeScript's weak-type check requires when every property is optional.
        formattedPaymentAmountPerInterval?: string | null;
    };
};

type PaymentOptionsModalProps = {
    paymentOptions: PaymentOptionLike[];
    onClose: () => void;
};

/*
    The grouping rule: the regular monthly plan, the extended monthly plan and every
    third-party financing option share one explanation, added once. The accelerated monthly plans are
    also monthly by frequency but have their own explanations, so they are not grouped here.
*/
export function PaymentOptionsModal({ paymentOptions, onClose }: PaymentOptionsModalProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const { loadFaqUrl } = navigationHelper();

    const sections: { key: string; headingKey: string; textKey: string; values: Record<string, unknown> }[] = [];
    let hasAddedMonthlyPlanExplanation = false;

    paymentOptions.forEach(option => {
        const frequency = option.tuitionPlan?.frequency;
        const isGroupedMonthly =
            (frequency !== undefined &&
                [TuitionPlanFrequency.Monthly, TuitionPlanFrequency.MonthlyExtended].includes(frequency)) ||
            !!option.additionalFinancingOption;

        if (isGroupedMonthly) {
            if (hasAddedMonthlyPlanExplanation) return;
            hasAddedMonthlyPlanExplanation = true;
            sections.push({
                key: 'monthly',
                headingKey: `${LOCALE_PREFIX}.monthly_options`,
                textKey: `${LOCALE_PREFIX}.monthly_plan_explanation`,
                values: {},
            });
            return;
        }

        if (!frequency) return;

        const withDiscount = (option.paymentInformation.netDiscountAmount ?? 0) > 0 ? '_with_discount' : '';
        sections.push({
            key: frequency,
            headingKey: `${LOCALE_PREFIX}.${frequency}_option`,
            textKey: `${LOCALE_PREFIX}.${frequency}_plan_explanation${withDiscount}`,
            values: {
                additionalSavings: option.paymentInformation.formattedNetDiscountAmount ?? '',
                numIntervals: option.tuitionPlan?.numIntervals ?? 0,
            },
        });
    });

    return (
        <Modal titleKey={`${LOCALE_PREFIX}.payment_options`} size="lg" onClose={onClose}>
            <div className="flex flex-col gap-4 px-7.5 pb-7.5">
                {sections.map(section => (
                    <div key={section.key}>
                        <Text as="h3" variant="h3" className="mb-1">
                            {t(section.headingKey)}
                        </Text>
                        <Text as="p" className="mb-0 text-sm">
                            {t(section.textKey, section.values)}
                        </Text>
                    </div>
                ))}

                <Text as="p" className="mb-0 text-sm">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.refund_policy`}
                        components={{
                            refundPolicyLink: (
                                <InlineLink
                                    className="text-sm"
                                    onPress={() => loadFaqUrl('/help/tuition-refund-policy', '_blank')}
                                />
                            ),
                        }}
                    />
                </Text>
            </div>
        </Modal>
    );
}
